import { cloneDeep } from 'lodash';
import { INVENTORY_BLOCK_STATUSES } from './constants';
import { ROOM_KEYS, REVENUE_KEYS, INVENTORY_BLOCK_ALLOCATIONS_KEYS } from 'utils/analytics/types';
import { sumObjectsProperties, buildObjectFromKeys } from 'utils/objects';
import { compare } from 'utils/dates';

/**
 * Room allocation statistics breakdown
 * @typedef {Object} InventoryBlockAllocationsStats
 * @property {?Number} sellLimit
 * @property {Number} blockedRoomCount
 * @property {Number} pickedUpRoomCount
 * @property {Number} revenueEstimate
 * @property {Object} pickup - rooms & revenue breakdown
 */

/**
 * Inventory block record details with allocations
 * @typedef {InventoryBlockAllocationsStats} InventoryBlock
 * @property {String} code
 * @property {?String} name
 * @property {INVENTORY_BLOCK_STATUSES} status
 * @property {?String} startDate - YYYY-MM-DD formatted
 * @property {?String} endDate - YYYY-MM-DD formatted
 * @property {?String} marketCode
 * @property {?String} sourceOfBusiness
 * @property {?String} createdDateTime
 * @property {String} lastModifiedDateTime
 * @property {InventoryBlockRoomAllocation[]} allocations
 */

/**
 * Room allocations details for a specific date
 * @typedef {InventoryBlockAllocationsStats} InventoryBlockRoomAllocation
 * @property {String} stayDate - YYYY-MM-DD formatted
 */

/**
 * Format of the data used for exporting into a CSV file, containing both the blocks & allocations breakdown at once
 * @typedef {InventoryBlockAllocationsStats} InventoryBlockExport
 * @property {?String} code - set for row describing a block
 * @property {?String} name - set for row describing a block
 * @property {?String} startDate - set for row describing a block
 * @property {?String} endDate - set for row describing a block
 * @property {?String} marketCode - set for row describing a block
 * @property {?String} stayDate - set for row describing an allocation
 * @property {Number} blockedRoomCount
 * @property {Number} pickedUpRoomCount
 * @property {Number} availableRoomCount
 * @property {Number} revenueSold
 * @property {Number} estimatedRemainingRevenue
 * @property {Number} estimatedRevenueTotal
 */

/**
 * Filter out blocks that are cancelled or that have no allocations
 * @param  {InventoryBlock[]} blocks - blocks with calculated allocations totals
 * @return {InventoryBlock[]}
 */
const filterDisplayableBlocks = (blocks) =>
    blocks.filter(
        (row) =>
            !(
                row.status === INVENTORY_BLOCK_STATUSES.CANCELLED ||
                row.status === INVENTORY_BLOCK_STATUSES.LOST ||
                (row.blockedRoomCount === 0 && row.pickedUpRoomCount === 0)
            ),
    );

/**
 * Sort inventory blocks by startDate (ascending)
 * @param  {InventoryBlock[]} blocks
 * @return {InventoryBlock[]}
 */
const sortBlocksByStartDate = (blocks) => cloneDeep(blocks).sort((a, b) => compare(a.startDate, b.startDate));

/**
 * Calculate allocations totals of all blocks (blocks/pickups/etc)
 * @param  {InventoryBlockAllocationsStats[] | InventoryBlock[]} allocations - array of allocations, or array of blocks that contain allocations totals
 * @return {InventoryBlockAllocationsStats}
 */
const calculateBlockTotals = (allocations) => {
    const keys = [
        ...INVENTORY_BLOCK_ALLOCATIONS_KEYS,
        ...ROOM_KEYS.map((key) => `pickup.${key}`),
        ...REVENUE_KEYS.map((key) => `pickup.${key}`),
    ];
    return allocations.reduce((accu, row) => sumObjectsProperties(accu, row, keys), {
        ...buildObjectFromKeys(keys),
        sellLimit: null,
        blockedRoomCount: 0,
        pickedUpRoomCount: 0,
        revenueEstimate: null,
        revenueEstimateOriginal: null,
    });
};

/**
 * Calculate allocations totals for all provided blocks & return the array with inserted total
 * @param  {InventoryBlock[]} blocks
 * @return {InventoryBlock[]} blocks with totals
 */
const calculateAllocationsTotals = (blocks) =>
    blocks.map((row) => ({
        ...row,
        ...calculateBlockTotals(row.allocations),
    }));

/**
 * Extracts & calculates required allocations statistics from a generic allocation record
 * @param  {InventoryBlockAllocationsStats} allocationStats
 * @return {Object} an object with the 5 displayable stats:
 *      - blockedRoomCount - number of rooms allocated to block
 *      - pickedUpRoomCount - number of rooms sold
 *      - availableRoomCount - number of rooms left to pick up
 *      - revenueSold - revenue made on the sold rooms
 *      - estimatedRemainingRevenue - estimated revenue to be made on the available room count
 *      - estimatedRevenueTotal - revenue sold + est. remaining revenue
 */
const extractAllocationStatistics = (allocationStats) => {
    const { blockedRoomCount, pickedUpRoomCount, revenueEstimate, revenueEstimateOriginal, pickup } = allocationStats;
    const revenueSold = pickup.revenue.accommodation.guaranteed;
    const roomsSold = pickup.rooms.guaranteed;
    return {
        blockedRoomCount,
        pickedUpRoomCount,
        availableRoomCount: blockedRoomCount - roomsSold,
        revenueSold,
        roomsSold,
        estimatedRemainingRevenue: revenueEstimate,
        estimatedRevenueTotal:
            revenueEstimateOriginal != null ? revenueEstimateOriginal : revenueSold + (revenueEstimate || 0),
    };
};

/**
 * Extracts all required field for export & format in the shape of an array containing all blocks & all allocations
 * @param  {InventoryBlock[]} blocks
 * @return {InventoryBlockExport[]}
 */
const extractDataForExport = (blocks) =>
    blocks.reduce((accu, block) => {
        const blockRow = {
            code: block.code,
            name: block.name,
            startDate: block.startDate,
            endDate: block.endDate,
            marketCode: block.marketCode,
            status: block.status,
            ...extractAllocationStatistics(block),
        };
        const allocationsRows = block.allocations.map((row) => ({
            stayDate: row.stayDate,
            ...extractAllocationStatistics(row),
        }));
        return [...accu, blockRow, ...allocationsRows];
    }, []);

export {
    filterDisplayableBlocks,
    sortBlocksByStartDate,
    calculateBlockTotals,
    calculateAllocationsTotals,
    extractAllocationStatistics,
    extractDataForExport,
};
